<template>
  <div :class="wrapperClasses">
    <span v-if="$slots.left" class="flex items-center pl-3 text-ink-muted">
      <slot name="left" />
    </span>
    <input
      :id="resolvedId"
      :type="type"
      :name="name"
      :value="modelValue"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :aria-invalid="invalid ? 'true' : undefined"
      :aria-describedby="describedBy"
      :class="inputClasses"
      @input="onInput"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
    <span v-if="$slots.right" class="flex items-center pr-3 text-ink-muted">
      <slot name="right" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import { useId } from '@/composables/useId';

const inputWrapperVariants = cva(
  'group relative flex items-stretch rounded-md border bg-paper transition-colors duration-150',
  {
    variants: {
      invalid: {
        true: 'border-stroke-danger focus-within:shadow-[0_0_0_3px_rgba(229,57,53,0.25)]',
        false:
          'border-stroke focus-within:border-stroke-accent focus-within:shadow-[var(--shadow-ring-focus)]',
      },
      disabled: {
        true: 'opacity-60',
        false: '',
      },
    },
    defaultVariants: {
      invalid: false,
      disabled: false,
    },
  },
);

const inputFieldVariants = cva(
  'w-full min-w-0 bg-transparent px-4 text-ink outline-none placeholder:text-ink-subtle',
  {
    variants: {
      size: {
        md: 'h-11 text-base',
        lg: 'h-14 text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export type InputSize = NonNullable<VariantProps<typeof inputFieldVariants>['size']>;

interface Props {
  modelValue?: string;
  type?: 'text' | 'email' | 'tel' | 'url' | 'search' | 'number' | 'password';
  size?: InputSize;
  placeholder?: string;
  id?: string | null;
  name?: string;
  autocomplete?: string;
  inputmode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  disabled?: boolean;
  readonly?: boolean;
  invalid?: boolean;
  required?: boolean;
  describedBy?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  size: 'md',
  id: null,
  disabled: false,
  readonly: false,
  invalid: false,
  required: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'blur', event: FocusEvent): void;
  (e: 'focus', event: FocusEvent): void;
}>();

const fallbackId = useId('input');
const resolvedId = computed(() => props.id ?? fallbackId);

const wrapperClasses = computed(() =>
  inputWrapperVariants({ invalid: props.invalid, disabled: props.disabled }),
);

const inputClasses = computed(() => inputFieldVariants({ size: props.size }));

function onInput(event: Event): void {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
}
</script>
